<template>
	<div class="home">
		<main class="home__main">
			<AdaptiveContainer>
				<HiqHeader @click="onHome" />
				<Avatar class="home__main__avatar" ref="avatar" />
				<transition name="fade" mode="out-in">
					<!-- Loading campaign -->
					<template v-if="campaignPending">
						<div class="home__main__fixed">
							<Loading color="white" />
						</div>
					</template>

					<!-- Main content -->
					<template v-else-if="campaignLoaded">
						<router-view />
					</template>

					<!-- No campaign running -->
					<template v-else-if="campaignNotFound">
						<div class="home__main__error">
							<h2>Nothing to see here.</h2>
							<p>
								There's no campaign running at the moment. Check back later!
							</p>
						</div>
					</template>

					<!-- Campaign failed to load -->
					<template v-else>
						<div class="home__main__error">
							<h2>Uh oh!</h2>
							<p>Sorry, something went wrong. Check back again later.</p>
						</div>
					</template>
				</transition>
			</AdaptiveContainer>
		</main>

		<HiqFooter />

		<!-- Modals -->
		<ModalAbout />
		<ModalTerms />
	</div>
</template>

<script>
import { mapGetters } from 'vuex'
import { enums } from 'protoswipe-shared'
import campaign from '@/mixins/campaign'
import wait from '@/mixins/wait'
import Loading from '@/components/shared/Loading'
import HiqHeader from '@/components/shared/HiqHeader'
import HiqFooter from '@/components/main/HiqFooter'
import AdaptiveContainer from '@/components/main/AdaptiveContainer'
import ModalAbout from '@/components/main/ModalAbout'
import ModalTerms from '@/components/main/ModalTerms'
import Avatar from '@/components/avatar/Avatar'

const { Dialogue } = enums
const INITIAL_SPEAK_DELAY = 2000
const ROUTE_SPEAK_DELAY = 1000

export default {
	name: 'Home',
	computed: {
		...mapGetters({
			leaderboardLoaded: 'leaderboard/loadedAny',
			leaderboardResults: 'leaderboard/results',
			galleryLoaded: 'gallery/loadedAny',
			galleryResults: 'gallery/results'
		}),
		showIntro() {
			return !this.$store.state.intro.done
		},
		isHome() {
			return this.$route.name === 'Home'
		}
	},
	methods: {
		onHome() {
			if (!this.isHome) this.$router.push({ name: 'Home' })
		},
		async sayOnRoute(delay, { name } = this.$route) {
			const { avatar } = this.$refs
			if (!avatar) return false

			// Reset any current speech and wait for page transition
			await avatar.speechStop()
			await this.wait(delay)

			// Choose something to say after navigating based on route name
			switch (name) {
				case 'Home':
					// Greet first if not yet done
					if (this.showIntro) {
						this.$store.commit('intro/done')
						avatar.speechAdd(Dialogue.greeting, true)
					}
					// Read prompt
					avatar.speechAdd(Dialogue.question)
					break
				case 'Submit':
					avatar.speechAdd(Dialogue.submit, true)
					break
				case 'Submission successful':
					avatar.speechAdd(Dialogue.submitted)
					break
				case 'Voted':
					avatar.speechAdd(Dialogue.voted)
					break
				default:
					// Maybe a 404 message?
					break
			}
			avatar.speechStart()
		}
	},
	watch: {
		$route(to) {
			this.sayOnRoute(ROUTE_SPEAK_DELAY, to)
		},
		async leaderboardLoaded(loaded) {
			// Only show leaderboard speech once the leaderboard has loaded
			const { avatar } = this.$refs
			if (avatar && this.$route.name === 'Leaderboard' && loaded) {
				// Look down at page
				await this.wait(ROUTE_SPEAK_DELAY)
				await avatar.aLookDown()

				// Choose what to say based on number of results
				const text =
					this.leaderboardResults.length > 0
						? Dialogue.leaderboard
						: Dialogue.leaderboard_empty
				avatar.speechAdd(text, true)

				// Start talking
				avatar.speechStart()
			}
		},
		async galleryLoaded(loaded) {
			// Only show leaderboard speech once the leaderboard has loaded
			const { avatar } = this.$refs
			if (avatar && this.$route.name === 'Gallery' && loaded) {
				// Look down at page
				await this.wait(ROUTE_SPEAK_DELAY)
				await avatar.aLookDown()

				// Choose what to say based on number of results
				const text =
					this.galleryResults.length > 0
						? Dialogue.gallery
						: Dialogue.gallery_empty
				avatar.speechAdd(text, true)

				// Start talking
				avatar.speechStart()
			}
		}
	},
	mounted() {
		this.sayOnRoute(INITIAL_SPEAK_DELAY)
	},
	mixins: [campaign, wait],
	components: {
		Loading,
		HiqHeader,
		HiqFooter,
		AdaptiveContainer,
		ModalAbout,
		ModalTerms,
		Avatar
	}
}
</script>

<style lang="scss" scoped>
@import '~scss/vars';
@import '~scss/layout';
@import '~scss/components';
@import '~scss/transitions';
@import '~scss/mq';

@include fade(0.1s);

.home {
	@extend %flex-col;
	min-height: 100vh;

	&__main {
		background: $gradient-orange;
		overflow: hidden;

		&__avatar {
			height: 18rem;
		}

		&__fixed {
			height: $loading-h;
		}

		&__error {
			@include corridor;
			@include truncate-margin;
			color: $white;
			text-align: center;
			margin: 2rem auto;
		}

		@include mq(md) {
			&__avatar {
				height: 23rem;
			}
		}

		@include mq(lg) {
			&__avatar {
				height: 28rem;
			}
		}
	}
}
</style>
